import type { Patient, Reference } from '@medplum/fhirtypes'
import type { GoalCarePlan } from '@seen/core/models/care-plans/GoalCarePlan'
import type { QuestionnaireWithResponse } from '@seen/core/models/questionnaireResponse'
import * as questionnaires from '@seen/core/models/questionnaires/index'
import { DayCenterSchedule } from '@seen/core/models/schedules/dayCenterSchedule'
import { type JSX, useState } from 'react'
import { CarePlanDayCenterScheduleSummary } from '#src/components/care-plan/CarePlanDayCenterScheduleSummary.tsx'
import { CarePlanDemographicQuestionnaire } from '#src/components/care-plan/CarePlanDemographicQuestionnaire.tsx'
import { ConditionList } from '#src/components/condition/ConditionList.tsx'
import { DayCenterScheduleDialog } from '#src/components/patient/detail-content/day-center-schedule/DayCenterScheduleDialog.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

const DAY_CENTER_ATTENDANCE_LINK_ID = 'day-center-attendance'

export function CarePlanPatientDetailsDisplay({
  patient,
  goalCarePlans,
  demographics,
  readOnly = false,
  descriptionQuestionnaireUrls = [],
}: {
  patient: Reference<Patient>
  goalCarePlans: GoalCarePlan[]
  demographics: QuestionnaireWithResponse[]
  readOnly?: boolean
  // Questionnaires this care plan's Description already renders as embedded
  // nodes. They are dropped here so each one is authored in exactly one place.
  descriptionQuestionnaireUrls?: readonly string[]
}): JSX.Element {
  const [dayCenterDialogOpen, setDayCenterDialogOpen] = useState(false)
  const participant = useCurrentParticipant()
  const { data: dayCenterSchedule } = useSearchOne(
    DayCenterSchedule,
    DayCenterSchedule.queryByParticipant(patient)
  )

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h4>Conditions</h4>
        <ConditionList patient={patient} goalCarePlans={goalCarePlans} />
      </div>
      {demographics
        .filter(
          ({ questionnaire }) =>
            !descriptionQuestionnaireUrls.includes(questionnaire.url ?? '')
        )
        .map(({ questionnaire, questionnaireResponse }) => {
          const isServiceAllocation =
            questionnaire.url ===
            questionnaires.ServiceAllocationQuestionnaire.url

          return (
            <CarePlanDemographicQuestionnaire
              key={questionnaire.url ?? questionnaire.id}
              questionnaire={questionnaire}
              questionnaireResponse={questionnaireResponse}
              readOnly={readOnly}
              hideLinkIds={
                isServiceAllocation
                  ? [DAY_CENTER_ATTENDANCE_LINK_ID]
                  : undefined
              }
              prefix={
                isServiceAllocation ? (
                  <>
                    <CarePlanDayCenterScheduleSummary
                      schedule={dayCenterSchedule}
                      onClick={() => setDayCenterDialogOpen(true)}
                    />
                    <DayCenterScheduleDialog
                      open={dayCenterDialogOpen}
                      onOpenChange={setDayCenterDialogOpen}
                      participant={participant}
                      schedule={dayCenterSchedule ?? null}
                    />
                  </>
                ) : undefined
              }
            />
          )
        })}
    </div>
  )
}
